import type { Rect } from './boardGeometry'

/**
 * The house picture shape. Every Birdie clip is encoded 16:9 and `.marbles-race`
 * is itself `aspect-ratio: 16 / 9`, so the picture box is DERIVED from the stage
 * rather than measured off a `<video>` element (B30).
 *
 * That is the whole point of this module: the board's origin and its scale used
 * to be a function of the clip, so a stream that reported its size late moved
 * the board once, a stream that errored and remounted moved it again, and a
 * non-16:9 encode moved it for good. The stage is a constant; the clip is not.
 */
export const HOUSE_ASPECT = { width: 16, height: 9 }

/** The box an `object-fit: contain` picture of `aspect` paints inside `container`. */
export function containRect(
  container: { width: number; height: number },
  aspect: { width: number; height: number }
): Rect {
  const cw = Math.max(0, container.width)
  const ch = Math.max(0, container.height)
  if (cw === 0 || ch === 0) return { x: 0, y: 0, width: 0, height: 0 }

  const nw = aspect.width > 0 ? aspect.width : HOUSE_ASPECT.width
  const nh = aspect.height > 0 ? aspect.height : HOUSE_ASPECT.height
  const scale = Math.min(cw / nw, ch / nh)
  const width = nw * scale
  const height = nh * scale
  return { x: (cw - width) / 2, y: (ch - height) / 2, width, height }
}

/**
 * The picture box the board is registered to: the HOUSE aspect contain-fitted
 * inside the measured container, in coordinates relative to that container.
 *
 * On a 16:9 stage (which is what `.marbles-race` always is) this is the whole
 * container, so the board is pixel-identical to the frame the old measured path
 * converged on — minus the transient it used to show before the clip reported
 * itself. Never returns NaN: a zero or negative container is the empty rect,
 * which `frameIsUsable` refuses to draw on.
 */
export function houseFrame(container: { width: number; height: number }): Rect {
  return containRect(container, HOUSE_ASPECT)
}

export function frameIsUsable(frame: Rect): boolean {
  return frame.width >= 320 && frame.height >= 180
}
